"use client";

import { Button } from "@ethera/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@ethera/ui/dialog";
import { useTranslation } from "@guru/shared/i18n";
import { Download, Share } from "lucide-react";
import { useState } from "react";
import { usePWAInstall } from "@/hooks/use-pwa-install";

interface PWAInstallButtonProps {
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg";
  className?: string;
  showOnlyWhenInstallable?: boolean;
}

export function PWAInstallButton({
  variant = "outline",
  size = "sm",
  className,
  showOnlyWhenInstallable = true,
}: PWAInstallButtonProps) {
  const { t } = useTranslation();
  const { isInstallable, isInstalled, promptInstall, isIOS } = usePWAInstall();
  const [showIOSDialog, setShowIOSDialog] = useState(false);

  // Don't render if already installed
  if (isInstalled) return null;

  // Don't render if not installable and we require it
  if (showOnlyWhenInstallable && !isInstallable && !isIOS) return null;

  const handleClick = async () => {
    if (isIOS) {
      setShowIOSDialog(true);
    } else {
      await promptInstall();
    }
  };

  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={handleClick}>
        <Download className="w-4 h-4 mr-2" />
        {t.pwa?.install ?? "Install App"}
      </Button>

      {/* iOS Instructions Dialog */}
      <Dialog open={showIOSDialog} onOpenChange={setShowIOSDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.pwa?.iosTitle ?? "Install EtheraGuru"}</DialogTitle>
            <DialogDescription>
              {t.pwa?.iosDescription ?? "To install this app on your iOS device:"}
            </DialogDescription>
          </DialogHeader>
          <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground mt-4">
            <li className="flex items-center gap-2">
              {t.pwa?.iosStep1 ?? "Tap the Share button"}{" "}
              <Share className="w-4 h-4 inline text-foreground" />
            </li>
            <li>{t.pwa?.iosStep2 ?? "Scroll down and tap 'Add to Home Screen'"}</li>
            <li>{t.pwa?.iosStep3 ?? "Tap 'Add' to confirm"}</li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
